// react_frontend/src/Components/Modules/UserSetting/EditEmployee.js
import React, { useState, useRef, useContext, useEffect } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import {
  Box, Typography, Paper, Grid, TextField, Button, IconButton, Tooltip,
  Stack, Avatar, Divider, MenuItem, Select, FormControl, InputLabel,
  InputAdornment, Tabs, Tab, Alert, Chip, CircularProgress, Card, CardContent
} from "@mui/material";
import {
  FaUserTie, FaEnvelope, FaPhone, FaBriefcase, FaMapMarkerAlt,
  FaIdCard, FaCalendarAlt, FaDollarSign, FaClipboardList, FaInfoCircle,
  FaBuilding, FaTint, FaUsers, FaHeartbeat, FaUniversity, FaFileUpload,
  FaGraduationCap, FaMedal, FaClock, FaFileAlt, FaUserFriends,
  FaMoneyBillWave, FaCalendarCheck, FaArrowLeft, FaSave, FaTimes,
  FaCamera, FaFilePdf, FaPlus, FaTrash,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components (Same as AddEmployee)
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
  position: "relative",
  overflow: "hidden",
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: "white",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  marginBottom: theme.spacing(3),
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  minHeight: 56,
  fontSize: "0.875rem",
  "&.Mui-selected": { color: "#667eea" },
}));

const EditEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateEmployeeInList } = useContext(MotherContext);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  const [employmentType, setEmploymentType] = useState('permanent');
  const [employmentRule, setEmploymentRule] = useState(null);
  const [loadingRule, setLoadingRule] = useState(false);

  const [companyRules, setCompanyRules] = useState({ basic: 50, rent: 40, medical: 10, conv: 5 });
  const [breakdown, setBreakdown] = useState({ houseRent: 0, medical: 0, conveyance: 0, gross: 0 });

  const [formData, setFormData] = useState({
    employee_name: "", employee_email: "", employee_phone: "", nid: "",
    nid_passport_number: "", blood_group: "", employee_photo: null, address: "",
    emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relation: "",
    department: "", employee_designation: "", employment_type: "permanent",
    joining_date: "", contract_start: "", contract_end: "", shift_type: "day",
    basic_salary: "", gross_salary: 0, house_rent: 0, medical_allowance: 0, conveyance: 0,
    bank_name: "", bank_account_number: "", bank_routing_number: "", tin_number: "",
    cv_file: null, skills: [], education: [], experience: [],
  });

  const [newSkill, setNewSkill] = useState("");
  const [newEducation, setNewEducation] = useState({ degree: "", institution: "", year: "" });
  const [newExperience, setNewExperience] = useState({ company: "", position: "", duration: "", description: "" });

  const employmentTypes = [
    { label: "Permanent", value: "permanent" }, { label: "Probation", value: "probation" },
    { label: "Contractual", value: "contractual" }, { label: "Part-Time", value: "part_time" },
    { label: "Intern", value: "intern" }, { label: "Temporary", value: "temporary" },
    { label: "Freelance", value: "freelance" },
  ];
  const bloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const shiftTypes = [
    { label: "Day Shift", value: "day" }, { label: "Night Shift", value: "night" },
    { label: "Rotational", value: "rotational" }, { label: "Flexible", value: "flexible" },
  ];
  const emergencyRelations = ["Father", "Mother", "Spouse", "Sibling", "Child", "Friend", "Other"];

  // Fetch Company Rules & Employee Data
  useEffect(() => {
    const initData = async () => {
      try {
        const [companyRes, empRes] = await Promise.all([
          AxiosAuthInstance.get("company/profile").catch(() => ({ data: { company: {} } })),
          AxiosAuthInstance.get(`add_employee/${id}`)
        ]);

        const rules = companyRes.data.company?.salary_rules || {};
        setCompanyRules({
          basic: rules.basic_percent ?? 50,
          rent: rules.house_rent_percent ?? 40,
          medical: rules.medical_percent ?? 10,
          conv: rules.conveyance_percent ?? 5,
        });

        const emp = empRes.data.employee;
        const skills = typeof emp.skills === 'string' ? JSON.parse(emp.skills) : (emp.skills || []);
        const education = typeof emp.education === 'string' ? JSON.parse(emp.education) : (emp.education || []);
        const experience = typeof emp.experience === 'string' ? JSON.parse(emp.experience) : (emp.experience || []);

        setFormData({
          employee_name: emp.employee_name || "",
          employee_email: emp.employee_email || "",
          employee_phone: emp.employee_phone || "",
          nid: emp.nid || "",
          nid_passport_number: emp.nid_passport_number || "",
          blood_group: emp.blood_group || "",
          employee_photo: null,
          address: emp.address || "",
          emergency_contact_name: emp.emergency_contact_name || "",
          emergency_contact_phone: emp.emergency_contact_phone || "",
          emergency_contact_relation: emp.emergency_contact_relation || "",
          department: emp.department || "",
          employee_designation: emp.employee_designation || "",
          employment_type: emp.employment_type || "permanent",
          joining_date: emp.joining_date ? emp.joining_date.split('T')[0] : "",
          contract_start: emp.contract_start ? emp.contract_start.split('T')[0] : "",
          contract_end: emp.contract_end ? emp.contract_end.split('T')[0] : "",
          shift_type: emp.shift_type || "day",
          basic_salary: emp.basic_salary || "",
          gross_salary: emp.gross_salary || 0,
          house_rent: emp.house_rent || 0,
          medical_allowance: emp.medical_allowance || 0,
          conveyance: emp.conveyance || 0,
          bank_name: emp.bank_name || "",
          bank_account_number: emp.bank_account_number || "",
          bank_routing_number: emp.bank_routing_number || "",
          tin_number: emp.tin_number || "",
          cv_file: null,
          skills, education, experience,
        });

        setEmploymentType(emp.employment_type || "permanent");
        setBreakdown({
          houseRent: parseFloat(emp.house_rent) || 0,
          medical: parseFloat(emp.medical_allowance) || 0,
          conveyance: parseFloat(emp.conveyance) || 0,
          gross: parseFloat(emp.gross_salary) || 0,
        });

      } catch (err) {
        console.error("Failed to load data:", err);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load employee data" });
        navigate("/hr/employees");
      } finally {
        setLoading(false);
      }
    };
    initData();
  }, [id, navigate]);

  // Fetch Employment Rule when type changes
  useEffect(() => {
    const fetchRule = async () => {
      if (!employmentType) return;
      setLoadingRule(true);
      try {
        const res = await AxiosAuthInstance.get(`company/employment-rules/type/${employmentType}`);
        if (res.data.status && res.data.rule) {
          setEmploymentRule(res.data.rule);
          if (res.data.rule.salary_rules) {
            setCompanyRules({
              basic: res.data.rule.salary_rules.basic_percent ?? 50,
              rent: res.data.rule.salary_rules.house_rent_percent ?? 40,
              medical: res.data.rule.salary_rules.medical_percent ?? 10,
              conv: res.data.rule.salary_rules.conveyance_percent ?? 5,
            });
          }
        } else {
          setEmploymentRule(null);
        }
      } catch (err) {
        setEmploymentRule(null);
      } finally {
        setLoadingRule(false);
      }
    };
    fetchRule();
  }, [employmentType]);

  const handleBasicChange = (val) => {
    const basic = parseFloat(val) || 0;
    const rent = (basic * companyRules.rent) / 100;
    const med = (basic * companyRules.medical) / 100;
    const conv = (basic * companyRules.conv) / 100;
    const gross = basic + rent + med + conv;

    setBreakdown({ houseRent: rent, medical: med, conveyance: conv, gross });
    setFormData(prev => ({
      ...prev, basic_salary: basic, gross_salary: gross, house_rent: rent, medical_allowance: med, conveyance: conv,
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleFileChange = (field, file) => {
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  const addSkill = () => {
    if (newSkill.trim()) {
      setFormData(prev => ({ ...prev, skills: [...prev.skills, newSkill.trim()] }));
      setNewSkill("");
    }
  };
  const removeSkill = (index) => setFormData(prev => ({ ...prev, skills: prev.skills.filter((_, i) => i !== index) }));

  const addEducation = () => {
    if (newEducation.degree && newEducation.institution) {
      setFormData(prev => ({ ...prev, education: [...prev.education, { ...newEducation }] }));
      setNewEducation({ degree: "", institution: "", year: "" });
    }
  };
  const removeEducation = (index) => setFormData(prev => ({ ...prev, education: prev.education.filter((_, i) => i !== index) }));

  const addExperience = () => {
    if (newExperience.company && newExperience.position) {
      setFormData(prev => ({ ...prev, experience: [...prev.experience, { ...newExperience }] }));
      setNewExperience({ company: "", position: "", duration: "", description: "" });
    }
  };
  const removeExperience = (index) => setFormData(prev => ({ ...prev, experience: prev.experience.filter((_, i) => i !== index) }));

  const validateForm = () => {
    const newErrors = {};
    if (!formData.employee_name.trim()) newErrors.employee_name = "Name is required";
    if (!formData.employee_email.trim()) newErrors.employee_email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.employee_email)) newErrors.employee_email = "Invalid email";
    if (!formData.employee_phone.trim()) newErrors.employee_phone = "Phone is required";
    if (!formData.nid.trim()) newErrors.nid = "NID is required";
    if (!formData.employee_designation.trim()) newErrors.employee_designation = "Designation is required";
    if (!formData.joining_date) newErrors.joining_date = "Joining date is required";
    if (!formData.basic_salary || formData.basic_salary <= 0) newErrors.basic_salary = "Basic salary is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      Swal.fire({ icon: "warning", title: "Validation Error", text: "Please fill all required fields" });
      return;
    }

    setSubmitting(true);
    const formDataToSend = new FormData();
    
    // Append all scalar fields
    Object.keys(formData).forEach(key => {
      if (key !== 'skills' && key !== 'education' && key !== 'experience' && key !== 'employee_photo' && key !== 'cv_file') {
        formDataToSend.append(key, formData[key] || "");
      }
    });
    
    // Append arrays
    formData.skills.forEach((skill, index) => formDataToSend.append(`skills[${index}]`, skill));
    formData.education.forEach((edu, index) => {
      formDataToSend.append(`education[${index}][degree]`, edu.degree);
      formDataToSend.append(`education[${index}][institution]`, edu.institution);
      formDataToSend.append(`education[${index}][year]`, edu.year);
    });
    formData.experience.forEach((exp, index) => {
      formDataToSend.append(`experience[${index}][company]`, exp.company);
      formDataToSend.append(`experience[${index}][position]`, exp.position);
      formDataToSend.append(`experience[${index}][duration]`, exp.duration);
      formDataToSend.append(`experience[${index}][description]`, exp.description);
    });
    
    if (formData.employee_photo) formDataToSend.append("employee_photo", formData.employee_photo);
    if (formData.cv_file) formDataToSend.append("cv_file", formData.cv_file);

    try {
      const res = await AxiosAuthInstance.post(`add_employee/${id}`, formDataToSend, { // Laravel supports POST with _method=PUT or we can use put
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (res.data.status === true) {
        Swal.fire({ icon: "success", title: "Success!", text: "Employee updated successfully!", timer: 2000, showConfirmButton: false });
        if (res.data.employee) updateEmployeeInList(res.data.employee);
        navigate("/hr/employees");
      }
    } catch (err) {
      const serverErrors = err.response?.data?.errors || {};
      const formattedErrors = {};
      Object.keys(serverErrors).forEach(key => { formattedErrors[key] = serverErrors[key][0]; });
      setErrors(formattedErrors);
      Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || "Failed to update employee" });
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { label: "Personal", icon: FaUserTie }, { label: "Contact", icon: FaPhone },
    { label: "Job Info", icon: FaBriefcase }, { label: "Salary", icon: FaMoneyBillWave },
    { label: "Bank", icon: FaUniversity }, { label: "Documents", icon: FaFileAlt },
    { label: "Skills", icon: FaMedal },
  ];

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Edit Employee" />

      <GradientPaper elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", zIndex: 1 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaUserTie style={{ marginRight: 10, verticalAlign: "middle" }} /> Edit Employee
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>Update employee details and save changes</Typography>
          </Box>
          <Button variant="contained" startIcon={<FaArrowLeft />} onClick={() => navigate("/hr/employees")} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
            Back to List
          </Button>
        </Box>
      </GradientPaper>

      <Paper sx={{ mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: "divider", "& .MuiTabs-indicator": { background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", height: 3 } }}>
          {tabs.map((tab, index) => {
            const IconComponent = tab.icon;
            return <StyledTab key={index} icon={<IconComponent style={{ marginRight: 6 }} />} iconPosition="start" label={tab.label} />;
          })}
        </Tabs>
      </Paper>

      <SectionCard>
        {/* Personal Information */}
        {activeTab === 0 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2", mr: 2 }}><FaUserTie /></Avatar>
              <Box><Typography variant="h6" fontWeight={600}>Personal Information</Typography><Typography variant="body2" color="text.secondary">Basic employee details</Typography></Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
                <Box sx={{ width: 150, height: 150, borderRadius: "50%", bgcolor: "#f5f5f5", mx: "auto", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", border: "3px dashed #bdbdbd", transition: "all 0.3s ease", "&:hover": { borderColor: "#667eea", bgcolor: "#f0f2ff" } }} onClick={() => document.getElementById("photo-upload").click()}>
                  {formData.employee_photo ? (
                    <img src={URL.createObjectURL(formData.employee_photo)} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <Box sx={{ textAlign: "center" }}><FaCamera size={32} color="#bdbdbd" /><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>Upload New Photo</Typography></Box>
                  )}
                </Box>
                <input type="file" id="photo-upload" accept="image/*" style={{ display: "none" }} onChange={(e) => handleFileChange("employee_photo", e.target.files[0])} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>Leave empty to keep current photo</Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}><TextField fullWidth label="Full Name *" value={formData.employee_name} onChange={(e) => handleInputChange("employee_name", e.target.value)} error={!!errors.employee_name} helperText={errors.employee_name} InputProps={{ startAdornment: <InputAdornment position="start"><FaUserTie style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Email Address *" type="email" value={formData.employee_email} onChange={(e) => handleInputChange("employee_email", e.target.value)} error={!!errors.employee_email} helperText={errors.employee_email} InputProps={{ startAdornment: <InputAdornment position="start"><FaEnvelope style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Phone Number *" value={formData.employee_phone} onChange={(e) => handleInputChange("employee_phone", e.target.value)} error={!!errors.employee_phone} helperText={errors.employee_phone} InputProps={{ startAdornment: <InputAdornment position="start"><FaPhone style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="NID Number *" value={formData.nid} onChange={(e) => handleInputChange("nid", e.target.value)} error={!!errors.nid} helperText={errors.nid} InputProps={{ startAdornment: <InputAdornment position="start"><FaIdCard style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Passport Number" value={formData.nid_passport_number} onChange={(e) => handleInputChange("nid_passport_number", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaIdCard style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
                  <Grid item xs={12} md={6}><FormControl fullWidth><InputLabel>Blood Group</InputLabel><Select value={formData.blood_group} onChange={(e) => handleInputChange("blood_group", e.target.value)} label="Blood Group" startAdornment={<InputAdornment position="start"><FaTint style={{ color: "#f44336" }} /></InputAdornment>}>{bloodGroups.map((bg) => (<MenuItem key={bg} value={bg}>{bg}</MenuItem>))}</Select></FormControl></Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Contact & Emergency */}
        {activeTab === 1 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", mr: 2 }}><FaPhone /></Avatar>
              <Box><Typography variant="h6" fontWeight={600}>Contact & Emergency Information</Typography><Typography variant="body2" color="text.secondary">Address and emergency contacts</Typography></Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12}><TextField fullWidth label="Full Address *" multiline rows={3} value={formData.address} onChange={(e) => handleInputChange("address", e.target.value)} error={!!errors.address} helperText={errors.address} InputProps={{ startAdornment: <InputAdornment position="start"><FaMapMarkerAlt style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
              <Grid item xs={12}><Alert severity="info" icon={<FaHeartbeat />}>Emergency contact information is important for workplace safety</Alert></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Emergency Contact Name" value={formData.emergency_contact_name} onChange={(e) => handleInputChange("emergency_contact_name", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaUserFriends style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Emergency Contact Phone" value={formData.emergency_contact_phone} onChange={(e) => handleInputChange("emergency_contact_phone", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaPhone style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={4}><FormControl fullWidth><InputLabel>Relation</InputLabel><Select value={formData.emergency_contact_relation} onChange={(e) => handleInputChange("emergency_contact_relation", e.target.value)} label="Relation">{emergencyRelations.map((rel) => (<MenuItem key={rel} value={rel}>{rel}</MenuItem>))}</Select></FormControl></Grid>
            </Grid>
          </Box>
        )}

        {/* Job Information */}
        {activeTab === 2 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#fff3e0", color: "#ef6c00", mr: 2 }}><FaBriefcase /></Avatar>
              <Box><Typography variant="h6" fontWeight={600}>Job & Employment Information</Typography><Typography variant="body2" color="text.secondary">Position and employment details</Typography></Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}><TextField fullWidth label="Department" value={formData.department} onChange={(e) => handleInputChange("department", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaBuilding style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Designation *" value={formData.employee_designation} onChange={(e) => handleInputChange("employee_designation", e.target.value)} error={!!errors.employee_designation} helperText={errors.employee_designation} InputProps={{ startAdornment: <InputAdornment position="start"><FaBriefcase style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Employment Type *</InputLabel>
                  <Select value={employmentType} onChange={(e) => { setEmploymentType(e.target.value); handleInputChange("employment_type", e.target.value); }} label="Employment Type *" startAdornment={<InputAdornment position="start"><FaClipboardList style={{ color: "#757575" }} /></InputAdornment>}>
                    {employmentTypes.map((type) => (<MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>))}
                  </Select>
                </FormControl>
                {loadingRule && <Typography variant="caption" color="info" sx={{ mt: 0.5 }}>Loading employment rules...</Typography>}
                {employmentRule && !loadingRule && <Alert severity="success" sx={{ mt: 1 }}>✓ {employmentRule.probation_months || 0} months probation, {employmentRule.resignation_notice_days || 30} days notice</Alert>}
              </Grid>
              <Grid item xs={12} md={6}><FormControl fullWidth><InputLabel>Shift Type</InputLabel><Select value={formData.shift_type} onChange={(e) => handleInputChange("shift_type", e.target.value)} label="Shift Type" startAdornment={<InputAdornment position="start"><FaClock style={{ color: "#757575" }} /></InputAdornment>}>{shiftTypes.map((shift) => (<MenuItem key={shift.value} value={shift.value}>{shift.label}</MenuItem>))}</Select></FormControl></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Joining Date *" type="date" value={formData.joining_date} onChange={(e) => handleInputChange("joining_date", e.target.value)} error={!!errors.joining_date} helperText={errors.joining_date} InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><FaCalendarAlt style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
              {(employmentType === "contractual" || employmentType === "temporary") && (<>
                <Grid item xs={12} md={4}><TextField fullWidth label="Contract Start" type="date" value={formData.contract_start} onChange={(e) => handleInputChange("contract_start", e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Contract End" type="date" value={formData.contract_end} onChange={(e) => handleInputChange("contract_end", e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
              </>)}
            </Grid>
          </Box>
        )}

        {/* Salary */}
        {activeTab === 3 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", mr: 2 }}><FaMoneyBillWave /></Avatar>
              <Box><Typography variant="h6" fontWeight={600}>Salary & Benefits</Typography><Typography variant="body2" color="text.secondary">Compensation details</Typography></Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}><TextField fullWidth label="Basic Salary *" type="number" value={formData.basic_salary} onChange={(e) => handleBasicChange(e.target.value)} error={!!errors.basic_salary} helperText={errors.basic_salary || "Gross salary will be auto-calculated"} InputProps={{ startAdornment: <InputAdornment position="start"><FaDollarSign style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
              <Grid item xs={12}>
                <Card sx={{ bgcolor: "#f8f9ff", border: "1px solid #e3f2fd" }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom><FaClipboardList style={{ marginRight: 6 }} /> Salary Breakdown Preview</Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6} md={3}><Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}><Typography variant="caption" color="text.secondary">Basic</Typography><Typography variant="h6" fontWeight={700} color="#1976d2">৳{(formData.basic_salary || 0).toLocaleString()}</Typography></Box></Grid>
                      <Grid item xs={6} md={3}><Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}><Typography variant="caption" color="text.secondary">House Rent ({companyRules.rent}%)</Typography><Typography variant="h6" fontWeight={700} color="#2196f3">৳{breakdown.houseRent.toLocaleString()}</Typography></Box></Grid>
                      <Grid item xs={6} md={3}><Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}><Typography variant="caption" color="text.secondary">Medical ({companyRules.medical}%)</Typography><Typography variant="h6" fontWeight={700} color="#4caf50">৳{breakdown.medical.toLocaleString()}</Typography></Box></Grid>
                      <Grid item xs={6} md={3}><Box sx={{ textAlign: "center", p: 2, bgcolor: "white", borderRadius: 2 }}><Typography variant="caption" color="text.secondary">Conveyance ({companyRules.conv}%)</Typography><Typography variant="h6" fontWeight={700} color="#ff9800">৳{breakdown.conveyance.toLocaleString()}</Typography></Box></Grid>
                    </Grid>
                    <Box sx={{ mt: 2, p: 2, bgcolor: "#667eea", borderRadius: 2, textAlign: "center" }}>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)" }}>Gross Salary</Typography>
                      <Typography variant="h5" fontWeight={700} color="white">৳{breakdown.gross.toLocaleString()}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Bank Details */}
        {activeTab === 4 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#f3e5f5", color: "#7b1fa2", mr: 2 }}><FaUniversity /></Avatar>
              <Box><Typography variant="h6" fontWeight={600}>Bank & Financial Details</Typography><Typography variant="body2" color="text.secondary">Bank account and tax information</Typography></Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}><TextField fullWidth label="Bank Name" value={formData.bank_name} onChange={(e) => handleInputChange("bank_name", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaUniversity style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Account Number" value={formData.bank_account_number} onChange={(e) => handleInputChange("bank_account_number", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><FaIdCard style={{ color: "#757575" }} /></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Routing Number" value={formData.bank_routing_number} onChange={(e) => handleInputChange("bank_routing_number", e.target.value)} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="TIN Number" value={formData.tin_number} onChange={(e) => handleInputChange("tin_number", e.target.value)} helperText="Tax Identification Number" /></Grid>
            </Grid>
          </Box>
        )}

        {/* Documents */}
        {activeTab === 5 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#fce4ec", color: "#c2185b", mr: 2 }}><FaFileAlt /></Avatar>
              <Box><Typography variant="h6" fontWeight={600}>Documents</Typography><Typography variant="body2" color="text.secondary">Upload CV and other documents</Typography></Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ border: "2px dashed #bdbdbd", borderRadius: 2, p: 4, textAlign: "center", cursor: "pointer", transition: "all 0.3s ease", "&:hover": { borderColor: "#667eea", bgcolor: "#f5f7ff" } }} onClick={() => document.getElementById("cv-upload").click()}>
                  <input type="file" id="cv-upload" accept=".pdf,.doc,.docx" style={{ display: "none" }} onChange={(e) => handleFileChange("cv_file", e.target.files[0])} />
                  <FaFilePdf size={48} color="#bdbdbd" />
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 2 }}>{formData.cv_file ? formData.cv_file.name : "Click to upload new CV/Resume (or leave empty to keep current)"}</Typography>
                  <Typography variant="caption" color="text.secondary">PDF, DOC, DOCX (Max 5MB)</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Skills, Education, Experience */}
        {activeTab === 6 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "#fff3e0", color: "#ef6c00", mr: 2 }}><FaMedal /></Avatar>
              <Box><Typography variant="h6" fontWeight={600}>Skills, Education & Experience</Typography><Typography variant="body2" color="text.secondary">Professional background</Typography></Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Skills</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField size="small" placeholder="Add a skill..." value={newSkill} onChange={(e) => setNewSkill(e.target.value)} onKeyPress={(e) => e.key === "Enter" && addSkill()} sx={{ flex: 1 }} />
                <Button variant="contained" onClick={addSkill} startIcon={<FaPlus />}>Add</Button>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {formData.skills.map((skill, index) => (<Chip key={index} label={skill} onDelete={() => removeSkill(index)} color="primary" variant="outlined" />))}
              </Stack>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom><FaGraduationCap style={{ marginRight: 6 }} /> Education</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}><TextField size="small" fullWidth placeholder="Degree" value={newEducation.degree} onChange={(e) => setNewEducation({ ...newEducation, degree: e.target.value })} /></Grid>
                <Grid item xs={12} md={4}><TextField size="small" fullWidth placeholder="Institution" value={newEducation.institution} onChange={(e) => setNewEducation({ ...newEducation, institution: e.target.value })} /></Grid>
                <Grid item xs={12} md={2}><TextField size="small" fullWidth placeholder="Year" value={newEducation.year} onChange={(e) => setNewEducation({ ...newEducation, year: e.target.value })} /></Grid>
                <Grid item xs={12} md={2}><Button fullWidth variant="contained" onClick={addEducation} startIcon={<FaPlus />}>Add</Button></Grid>
              </Grid>
              <Stack spacing={1}>
                {formData.education.map((edu, index) => (
                  <Paper key={index} sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box><Typography variant="subtitle2" fontWeight={600}>{edu.degree}</Typography><Typography variant="body2" color="text.secondary">{edu.institution} {edu.year && `(${edu.year})`}</Typography></Box>
                    <IconButton color="error" onClick={() => removeEducation(index)}><FaTrash /></IconButton>
                  </Paper>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom><FaBriefcase style={{ marginRight: 6 }} /> Work Experience</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}><TextField size="small" fullWidth placeholder="Company" value={newExperience.company} onChange={(e) => setNewExperience({ ...newExperience, company: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField size="small" fullWidth placeholder="Position" value={newExperience.position} onChange={(e) => setNewExperience({ ...newExperience, position: e.target.value })} /></Grid>
                <Grid item xs={12} md={2}><TextField size="small" fullWidth placeholder="Duration" value={newExperience.duration} onChange={(e) => setNewExperience({ ...newExperience, duration: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField size="small" fullWidth placeholder="Description" value={newExperience.description} onChange={(e) => setNewExperience({ ...newExperience, description: e.target.value })} /></Grid>
                <Grid item xs={12} md={1}><Button fullWidth variant="contained" onClick={addExperience} startIcon={<FaPlus />}>Add</Button></Grid>
              </Grid>
              <Stack spacing={1}>
                {formData.experience.map((exp, index) => (
                  <Paper key={index} sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>{exp.position}</Typography>
                      <Typography variant="body2" color="text.secondary">{exp.company} {exp.duration && `• ${exp.duration}`}</Typography>
                      {exp.description && <Typography variant="caption" color="text.secondary">{exp.description}</Typography>}
                    </Box>
                    <IconButton color="error" onClick={() => removeExperience(index)}><FaTrash /></IconButton>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Box>
        )}
      </SectionCard>

      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate("/hr/employees")} startIcon={<FaTimes />} disabled={submitting} sx={{ textTransform: "none", fontWeight: 600, px: 3, borderRadius: 2 }}>Cancel</Button>
        <Stack direction="row" spacing={2}>
          {activeTab > 0 && <Button variant="outlined" onClick={() => setActiveTab(activeTab - 1)} sx={{ textTransform: "none", fontWeight: 600, px: 3, borderRadius: 2 }}>Previous</Button>}
          {activeTab < tabs.length - 1 ? (
            <Button variant="contained" onClick={() => setActiveTab(activeTab + 1)} sx={{ textTransform: "none", fontWeight: 600, px: 4, borderRadius: 2, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>Next</Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={submitting} startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <FaSave />} sx={{ textTransform: "none", fontWeight: 600, px: 4, borderRadius: 2, background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)", "&:hover": { background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)" } }}>
              {submitting ? "Updating..." : "Update Employee"}
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default EditEmployee;